import { useContext, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Divider,
  Dropdown,
  Icon,
  Message,
  Popup,
  Segment,
} from "semantic-ui-react";
import styled, { ThemeContext } from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { IconHeader } from "simplydo/react/components/IconHeader";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";
import { RichTextViewer } from "simplydo/react/richtext";

import actions from "actions";
import api, { asQuery } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import RedactedInfo from "components/lib/RedactedInfo";
import { FakeLink } from "components/lib/UI";

import ChallengeMyIdeas from "./Ideas/MyIdeas";
import ChallengeProblems from "./Problems";

const NewIdeaTrigger = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ChallengeClosedActions = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  .ui.button {
    ${({ theme }) =>
      theme.sizes.isMobile ?
        `
      &:not(:first-of-type) {
        margin-top: 10px;
      }
    `
      : `
      &:not(:first-of-type) {
        margin-left: 10px;
      }
    `}
  }
`;

const OverviewActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 25px;
  width: 100%;
`;

const OverviewActions = styled.div`
  display: flex;
  min-width: 66%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "center" : "flex-start"};
  justify-content: ${({ theme }) =>
    theme.sizes.isMobile ? "flex-start" : "space-evenly"};
  .ui.dropdown {
    height: 40px;
  }
  .ui.button,
  .ui.dropdown,
  ${NewIdeaTrigger} {
    flex-grow: 1;
    display: block;
    ${({ theme }) => theme.sizes.isMobile && "width: 100%;"}
    &:not(:last-of-type) {
      ${({ theme }) =>
        theme.sizes.isMobile ? "margin-bottom: 10px" : "margin-right: 5px"};
    }
  }
  ${NewIdeaTrigger} {
    display: flex;
  }
`;

type OverviewProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  newIdea: () => void;
  isPreview?: boolean;
  ipStatement?: OpenAPI.Schemas["IpStatement"];
  ipStatementHandled: boolean;
  ipManagementInfo?: { hasSensitiveInfo: boolean; userHasSigned: boolean };
  setIpStatementAgreementOpen: (open: boolean) => void;
  redirectToLogin: () => void;
};

const Overview = ({
  challenge = { name: "Challenge" },
  newIdea,
  ipStatement,
  ipStatementHandled,
  ipManagementInfo,
  setIpStatementAgreementOpen,
  redirectToLogin,
}: OverviewProps) => {
  const { t } = useTranslation();
  const [showMyIdeas, setShowMyIdeas] = useState(true);
  const theme = useContext(ThemeContext);
  const { video, videoUrl, reward, links, attachments, stage } = challenge;
  const {
    description = "",
    instructions,
    requirements,
    helpfulTips,
  } = challenge;
  const user = useAppSelector((state) => state.user);
  const canMakeIdea = util.canCreateIdea(user, challenge);

  const myIdeasQuery = useQuery({
    queryKey: ["challenges", challenge?._id, "ideas", user?._id],
    queryFn: asQuery(api.challenges.getIdeas, {
      params: [challenge?._id, { scope: "me" }],
    }),
    enabled: !!user?._id && !!challenge?._id,
  });
  const myIdeas = myIdeasQuery.data?.ideas || [];

  const myUnsubmittedIdeas = useMemo(
    () => myIdeas?.filter((idea) => !idea.isSubmitted),
    [myIdeas],
  );
  return (
    <div>
      <ChallengeProblems challenge={challenge} />
      {challenge.stage === "closed" ?
        <Message info>
          <Message.Header>
            This {t("generic.challenge")} is currently closed to new{" "}
            {t("generic.ideas")}
          </Message.Header>
          <ChallengeClosedActions>
            {(
              challenge.ideaVisibility === "users" ||
              util.canManageChallenge(user, challenge) ||
              util.hasPermission(user, "challenge.viewIdeas", challenge?._id)
            ) ?
              <Message.Content>
                You can still{" "}
                <Link to={`/challenges/${challenge._id}/ideas`}>
                  {challenge.votingVisibility === "users" ?
                    "view, like, and comment on"
                  : "view and comment on"}
                </Link>{" "}
                {t("generic.idea")} submissions.
                {(
                  challenge.projectManagementVisibility === "users" ||
                  util.canManageChallenge(user, challenge) ||
                  util.hasPermission(
                    user,
                    "challenge.viewProjectBoard",
                    challenge?._id,
                  )
                ) ?
                  <span>
                    {" "}
                    You may also{" "}
                    <Link
                      to={`/challenges/${challenge._id}/ideas?queryView=board`}
                    >
                      view the project board
                    </Link>{" "}
                    to view and manage the progress of {t("generic.idea")}{" "}
                    implementations.
                  </span>
                : null}
              </Message.Content>
            : null}
          </ChallengeClosedActions>
        </Message>
      : null}
      <ChallengeMyIdeas
        challengeId={challenge._id}
        showMyIdeas={showMyIdeas}
        setShowMyIdeas={setShowMyIdeas}
        style={{ marginBottom: 20 }}
      />
      {(video || description) && (
        <h3
          className="challenge-tour-step-2"
          style={{ textTransform: "uppercase" }}
        >
          {t("challenge.details.overview")}
        </h3>
      )}
      {video && (
        <div>
          <VideoPlayer src={videoUrl} canExpand />
          <Divider hidden />
        </div>
      )}
      {description && (
        <div>
          <RichTextViewer content={description} />
          <Divider hidden />
        </div>
      )}

      {reward && (
        <div>
          <Segment>
            <IconHeader
              as="h3"
              icon="check circle outline"
              header={t("challenge.details.benefits")}
              content={<RichTextViewer content={reward} />}
              wrapped={theme.sizes.isMobile}
            />
          </Segment>
          <Divider section />
        </div>
      )}

      {instructions && (
        <div>
          <h3 style={{ textTransform: "uppercase" }}>
            {t("challenge.details.instructions")}
          </h3>
          <RichTextViewer content={instructions} />
          <Divider section />
        </div>
      )}

      {requirements && (
        <div>
          <IconHeader
            as="h3"
            icon="envelope open outline"
            header={t("challenge.details.include")}
            content={<RichTextViewer content={requirements} />}
            wrapped={theme.sizes.isMobile}
          />
          <Divider section />
        </div>
      )}

      {helpfulTips && (
        <div>
          <IconHeader
            as="h3"
            icon="info circle"
            header={t("challenge.details.tips")}
            content={<RichTextViewer content={helpfulTips} />}
            wrapped={theme.sizes.isMobile}
          />
          <Divider section />
        </div>
      )}

      {((links && links.length > 0) ||
        (attachments && attachments.length > 0)) && (
        <div>
          <IconHeader
            as="h3"
            icon="folder"
            header={t("challenge.links.title")}
            content={
              <div>
                {links?.map((link) => (
                  <p key={link.link} style={{ marginBottom: 10 }}>
                    <strong>{link.title}</strong>
                    <br />
                    <a
                      className="with-border"
                      href={link.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.link}
                    </a>
                  </p>
                ))}
                {attachments?.map((a) => (
                  <p key={a.title} style={{ marginBottom: 10 }}>
                    <strong>{a.title}</strong>
                    <br />
                    <a
                      className="with-border"
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {a.name}
                    </a>
                  </p>
                ))}
              </div>
            }
          />
          <Divider hidden />
        </div>
      )}

      {ipManagementInfo?.hasSensitiveInfo &&
        util.organisationFeaturesEnabled(user, ["ipManagement"]) && (
          <Message info>
            <h3>ADDITIONAL INFORMATION</h3>
            {!ipManagementInfo.userHasSigned ?
              <>
                <p>
                  <Icon name="info" />{" "}
                  <strong>
                    This information will be available once you have agreed to
                    and signed the IP statement for this challenge.
                  </strong>
                </p>
                <OverviewActionsContainer
                  style={{ alignItems: "flex-start", marginBottom: 0 }}
                >
                  {[...Array(6)].map((_e, i) => (
                    <RedactedInfo key={i} width={Math.random() * 25 + 60} />
                  ))}
                </OverviewActionsContainer>
              </>
            : <>
                <p>
                  <strong>
                    <Icon name="info" /> You can read this information because
                    you have signed the IP statement for this challenge.
                  </strong>
                </p>
                <RichTextViewer content={ipStatement?.statementSensitiveInfo} />
              </>
            }
            <Button
              secondary
              onClick={() => setIpStatementAgreementOpen(true)}
              icon="file alternate"
              content="View IP statement"
              style={{ marginTop: 10 }}
            />
          </Message>
        )}

      {stage === "published" && !user && (
        <OverviewActionsContainer>
          <OverviewActions>
            {challenge.ideaVisibility === "users" && (
              <Button
                style={{
                  border: `1px solid ${theme.primaryColour}`,
                  color: theme.primaryColour,
                  backgroundColor: "#fff",
                  marginBottom: 5,
                }}
                icon="unhide"
                content={t("challenge.viewOtherIdeas")}
                as={Link}
                to={`/challenges/${challenge._id}/ideas`}
              />
            )}

            <NewIdeaTrigger>
              <Button
                primary
                icon="plus"
                content={
                  challenge.buttonText ?
                    challenge.buttonText
                  : t("challenge.ideas.createYourIdea")
                }
                onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
              />
              <Popup
                trigger={
                  <div style={{ alignSelf: "center", marginTop: 3 }}>
                    <FakeLink withBorder>
                      <Icon name="question circle" />
                      {t("challenge.details.ideaHelpTitle")}
                    </FakeLink>
                  </div>
                }
                content={t("challenge.details.ideaHelpDescription")}
              />
            </NewIdeaTrigger>

            {myUnsubmittedIdeas?.length === 1 && (
              <Button
                icon="lightbulb"
                content={t("challenge.continueIdea")}
                primary
                as={Link}
                to={`/ideas/${myUnsubmittedIdeas[0]._id}`}
              />
            )}

            {myUnsubmittedIdeas?.length > 1 && (
              <Dropdown
                icon={null}
                trigger={
                  <Button
                    primary
                    icon="lightbulb"
                    content={t("challenge.continueIdea")}
                  />
                }
              >
                <Dropdown.Menu>
                  {myUnsubmittedIdeas.map((i) => (
                    <Dropdown.Item
                      key={i._id}
                      as={Link}
                      to={`/ideas/${i._id}`}
                      content={i.name}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </OverviewActions>
        </OverviewActionsContainer>
      )}

      {stage === "published" && user && (
        <OverviewActionsContainer>
          <OverviewActions>
            {(
              (challenge.ideaCount >= 1 &&
                challenge.ideaVisibility === "users") ||
              util.canManageChallenge(user, challenge) ||
              util.hasPermission(user, "challenge.viewIdeas", challenge?._id)
            ) ?
              <Button
                inverted
                basic
                primary
                icon="unhide"
                content={t("challenge.viewOtherIdeas")}
                as={Link}
                to={`/challenges/${challenge._id}/ideas`}
                className="challenge-tour-step-3"
              />
            : null}

            {(
              canMakeIdea &&
              (!myUnsubmittedIdeas || myUnsubmittedIdeas?.length === 0)
            ) ?
              <Button
                primary
                icon="plus"
                content={
                  challenge.buttonText ?
                    challenge.buttonText
                  : t("challenge.ideas.createYourIdea")
                }
                onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
                disabled={!ipStatementHandled}
                className="challenge-tour-step-3"
              />
            : null}

            {myUnsubmittedIdeas?.length === 1 && (
              <Button
                icon="lightbulb"
                content={t("challenge.continueIdea")}
                primary
                as={Link}
                to={`/ideas/${myUnsubmittedIdeas[0]._id}`}
              />
            )}

            {myUnsubmittedIdeas?.length > 1 && (
              <Dropdown
                icon={null}
                trigger={
                  <Button
                    style={{ width: "100%" }}
                    primary
                    icon="lightbulb"
                    content={t("challenge.continueIdea")}
                  />
                }
              >
                <Dropdown.Menu>
                  {myUnsubmittedIdeas.map((i) => (
                    <Dropdown.Item
                      key={i._id}
                      as={Link}
                      to={`/ideas/${i._id}`}
                      content={i.name}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </OverviewActions>
          {(
            util.canManageChallenge(user, challenge) ||
            util.hasPermission(user, "challenge.editSettings", challenge?._id)
          ) ?
            <Input
              style={{ marginTop: 10 }}
              label="Login link"
              value={util.getBaseUrl(user?.ownerOrganisation)}
              readOnly
            />
          : null}
        </OverviewActionsContainer>
      )}
    </div>
  );
};

const mapStateToProps = (state, props) => ({
  user: props.isPreview ? {} : state.user,
});
const mapDispatchToProps = (dispatch) => ({
  updateFilters: (data) => dispatch(actions.challenges.updateIdeaFilters(data)),
});
const ChallengeOverviewContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Overview);

export default ChallengeOverviewContainer;
